import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CheckSquare, MessageSquareWarning, CalendarClock } from "lucide-react";

const TH = "text-left text-[10px] uppercase tracking-wider text-[#6B7280] px-2 py-1.5 border-b border-gray-200";
const TD = "px-2 py-1.5 text-xs text-[#0E2240] border-b border-gray-100";

function SlaBadge({ v }) {
  if (!v) return null;
  const c = v === "Breached" ? "#BB162B" : v === "At Risk" ? "#D97706" : v === "On Hold" ? "#6B7280" : "#1B7F3B";
  return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm text-white" style={{ backgroundColor: c }}>{v}</span>;
}

export default function BranchDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/analytics/branch").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  const chips = [
    { key: "approvals_pending", label: "Approvals waiting on me", icon: CheckSquare, to: "/requisitions/inbox", color: "#D97706" },
    { key: "feedback_overdue", label: "Interview feedback overdue", icon: MessageSquareWarning, to: "/interviews", color: "#BB162B" },
    { key: "slots_not_given", label: "Interview slots not given", icon: CalendarClock, to: "/requisitions", color: "#1E5AA8" },
  ];

  return (
    <div data-testid="branch-dashboard">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Branch Command</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#0E2240] tracking-tight mt-1">Welcome, {user?.full_name?.split(" ")[0]}</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {chips.map(({ key, label, icon: Icon, to, color }) => {
          const v = d?.pendency?.[key] ?? null;
          return (
            <Link key={key} to={to} data-testid={`pendency-${key}`}
              className={`bg-white border rounded-sm p-4 flex items-center gap-3 hover:border-[#0E2240] transition-colors duration-200 ${v > 0 ? "border-current" : "border-gray-200"}`}
              style={v > 0 ? { borderColor: color } : {}}>
              <Icon size={20} strokeWidth={1.75} style={{ color }} />
              <div>
                <div className="text-2xl font-semibold tabular-nums" style={{ color: v > 0 ? color : "#0E2240" }}>{v ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5 mb-6" data-testid="branch-indents">
        <h2 className="font-serif text-base text-[#0E2240] mb-3">My indents — where each one stands</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className={TH}>MRF</th><th className={TH}>Role</th><th className={TH}>Status</th><th className={TH}>Pending With</th><th className={TH}>Days</th><th className={TH}>SLA</th></tr></thead>
            <tbody>
              {(d?.indents || []).map((r) => (
                <tr key={r.req_id}>
                  <td className={TD}><Link to={`/requisitions/${r.req_id}`} className="text-[#1E5AA8] hover:underline">{r.req_no}</Link></td>
                  <td className={TD}>{r.desig_name}</td>
                  <td className={TD}>{r.status}{r.stage && r.stage !== r.status ? ` · ${r.stage}` : ""}</td>
                  <td className={TD}>{r.pending_with || "—"}</td>
                  <td className={`${TD} tabular-nums ${r.days_open > 45 ? "text-[#BB162B] font-semibold" : ""}`}>{r.days_open}</td>
                  <td className={TD}><SlaBadge v={r.sla_status} /></td>
                </tr>
              ))}
              {d && d.indents.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-xs text-[#6B7280]">No open indents</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5 mb-8" data-testid="branch-grid">
        <h2 className="font-serif text-base text-[#0E2240] mb-3">Sanction vs actual — my branch</h2>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full">
            <thead><tr><th className={TH}>Department</th><th className={TH}>Designation</th><th className={TH}>Grade</th><th className={TH}>Sanctioned</th><th className={TH}>Active</th><th className={TH}>Gap</th><th className={TH}>Open Reqs</th></tr></thead>
            <tbody>
              {(d?.grid || []).map((r, i) => (
                <tr key={i}>
                  <td className={TD}>{r.dept_name}</td>
                  <td className={TD}>{r.desig_name}</td>
                  <td className={TD}>{r.grade}</td>
                  <td className={`${TD} tabular-nums`}>{r.sanctioned}</td>
                  <td className={`${TD} tabular-nums`}>{r.active}</td>
                  <td className={`${TD} tabular-nums font-semibold ${r.gap > 0 ? "text-[#BB162B]" : r.gap < 0 ? "text-[#1E5AA8]" : "text-[#1B7F3B]"}`}>{r.gap}</td>
                  <td className={`${TD} tabular-nums`}>{r.open_reqs}</td>
                </tr>
              ))}
              {d && d.grid.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-xs text-[#6B7280]">No sanctioned positions in scope</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
